import * as React from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
  type QueryClient,
} from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import type { DutyBranch } from '@/features/duties/branches'
import { useDutyBranch } from '@/features/duties/components/DutyBranchContext'

import type {
  BookingToolName,
  DutyCallVolumeMonitor,
  DutyPatientMemoryDetail,
  DutyPatientMemoryPatch,
  DutyTestMemories,
  TestMemoryEntry,
  AppointmentType,
  DutyBookingProviders,
  DutyConnectionInfo,
  DutySmsConfig,
  DutyStatus,
  DutySystemScriptPatchArgs,
  DutySystemScriptPayload,
  DutyTextingConfig,
  DutyToolKey,
  DutyToolsInfo,
  DutyVoiceConfig,
  DutyWorkflowsInfo,
  DutyAssistantOverridesPatchArgs,
  WorkflowVariantsInfo,
} from '@/features/duties/types'
import {
  deleteDutyWorkflowVariant,
  fetchDutyWorkflowVariants,
  setDutyWorkflowVariantEnabled,
  upsertDutyWorkflowVariant,
  type UpsertVariantPayload,
} from '@/features/duties/api/variants'
import { TOOL_WORKFLOW_SLUGS } from '@/features/duties/components/detail/dutyCardNav'
import {
  ASSIGNMENT_LOGIC_QUERY,
  EXCLUSION_LOGIC_QUERY,
  LAST_ASSIGNMENT_CHANGE_QUERY,
  LAST_ASSIGNMENT_RUN_QUERY,
  LAST_EXCLUSION_CHANGE_QUERY,
  dutyQueryKey,
  dutyScriptQueryKey,
  dutySummaryKeys,
  snippetKeys,
  workflowKeys,
} from '@/lib/queryKeys'
import {
  fetchDutyConnectionInfo,
  fetchDutyBookingProviders,
  fetchDutyConversationMetrics,
  fetchDutyFactExtractionMetrics,
  fetchDutyFactsTemplate,
  fetchDutyLabelMappingMetrics,
  fetchDutyCallVolumeMonitor,
  fetchDutyResequenceAnalysisPrompt,
  fetchDutySmsConfig,
  fetchDutySystemScript,
  fetchDutyTextingConfig,
  fetchDutyTestPatientInfo,
  fetchDutyToolsInfo,
  fetchDutyWorkflows,
  invokeDutyWorkflow,
  fetchDutyVersions,
  fetchDutyVoicemailMetrics,
  fetchDutyWaypoints,
  generateDutyWaypoints,
  patchDutyWaypoints,
  fetchCompiledTemplates,
  fetchDutyAssistantOverrides,
  fetchDutyAssignmentLogic,
  fetchDutyLastAssignmentChange,
  fetchDutyLastAssignmentRun,
  fetchDutyLastExclusionChange,
  fetchDutyExclusionLogic,
  fetchDutyFirstMessage,
  fetchDutyPatientMemory,
  fetchDutyTestAssistantOverrides,
  patchDutyAssistantOverrides,
  patchDutyFirstMessage,
  patchDutyPatientMemory,
  fetchDutyTestMemories,
  patchDutyTestMemories,
  patchDutyHandoffConfig,
  patchDutyAuthenticatePersonConfig,
  patchDutyHandoffToDutyConfig,
  patchDutyBookingProviders,
  patchDutyTool,
  patchDutyConnection,
  type PatchDutyConnectionPayload,
  patchDutyConversationScript,
  patchDutyTestPatient,
  type DutyTestFactsSaveCheck,
  type PatchDutyTestPatientPayload,
  patchDutyFactExtractionScript,
  patchDutyFactsTemplateScript,
  patchDutyLabelMappingScript,
  patchDutyResequenceAnalysisPrompt,
  patchDutySmsConfig,
  patchDutySystemScript,
  patchDutyTextingConfig,
  patchDutyStatus,
  patchDutyName,
  patchDutyCallVolumeMonitor,
  patchDutyVoicemailScript,
  testDutyCallVolumeMonitorNotification,
  resolveDutyCallVolumeMonitor,
  type PatchDutyCallVolumeMonitorConfigPayload,
  type DutySummary,
} from '@/features/duties/api/api'

export type {
  DutyCallVolumeMonitor,
  DutyBookingProviders,
  DutyCallVolumeMonitorConfig,
  DutyConnectionInfo,
  DutyConnectionWindow,
  DutyConversationMetrics,
  DutyFactExtractionMetrics,
  DutyFactsMap,
  DutyFactsTemplateInfo,
  DutyHandoffType,
  DutyLabelMappingMetrics,
  DutyResequenceAnalysisPrompt,
  DutySmsConfig,
  DutySystemScriptPayload,
  DutySystemScriptPrompt,
  DutyTextingConfig,
  DutyTestPatientInfo,
  DutyToolKey,
  DutyToolsInfo,
  DutyTransferCallSettings,
  DutyVoicemailMetrics,
  DutyVoiceConfig,
  DutyAssistantOverrides,
  DutyAssistantOverridesPatchArgs,
} from '@/features/duties/types'

const NO_AUTH_TOKEN_ERROR = 'No authentication token available'

type DutyFetcher<T> = (
  token: string,
  dutyId: string,
  signal?: AbortSignal
) => Promise<T>

type DutyScriptFetcher<T> = (
  token: string,
  dutyId: string,
  branch: DutyBranch,
  signal?: AbortSignal
) => Promise<T>

const DEFAULT_DUTY_QUERY_STALE_TIME_MS = 30_000
const DEFAULT_DUTY_QUERY_GC_TIME_MS = 30 * 60_000

export function useUpdateDutyCallVolumeMonitor(dutyId: string) {
  return useAuthedDutyMutation<
    PatchDutyCallVolumeMonitorConfigPayload,
    DutyCallVolumeMonitor
  >(dutyId, (token, id, data) => patchDutyCallVolumeMonitor(token, id, data))
}

export function useTestDutyCallVolumeMonitorNotification(dutyId: string) {
  return useAuthedDutyMutation<
    void,
    {
      success: boolean
      provider_message_id?: string | null
      notification?: Record<string, unknown>
    }
  >(dutyId, (token, id) => testDutyCallVolumeMonitorNotification(token, id))
}

export function useResolveDutyCallVolumeMonitor(dutyId: string) {
  return useAuthedDutyMutation<void, DutyCallVolumeMonitor>(
    dutyId,
    (token, id) => resolveDutyCallVolumeMonitor(token, id)
  )
}

type AuthedDutyQueryOptions = {
  staleTime?: number
  gcTime?: number
  refetchOnWindowFocus?: boolean
  retry?: boolean | number
}

// ---- React Query hooks (one per card) ----

function useAuthedDutyQuery<T>(
  dutyId: string | undefined,
  suffix: string,
  fetcher: DutyFetcher<T>,
  enabled: boolean = true,
  options?: AuthedDutyQueryOptions
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const orgId = organization?.id ?? null

  return useQuery({
    queryKey: dutyQueryKey(orgId, dutyId ?? null, suffix),
    enabled: Boolean(enabled) && !!orgId && !!dutyId,
    staleTime: options?.staleTime ?? DEFAULT_DUTY_QUERY_STALE_TIME_MS,
    gcTime: options?.gcTime ?? DEFAULT_DUTY_QUERY_GC_TIME_MS,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    retry: options?.retry ?? 1,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      if (!dutyId) throw new Error('Missing duty id')
      return fetcher(token, dutyId, signal)
    },
  })
}

function useAuthedDutyScriptQuery<T>(
  dutyId: string | undefined,
  suffix: string,
  fetcher: DutyScriptFetcher<T>,
  enabled: boolean = true,
  options?: AuthedDutyQueryOptions
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const orgId = organization?.id ?? null
  const branch = useDutyBranch()

  return useQuery({
    queryKey: dutyScriptQueryKey(orgId, dutyId ?? null, branch, suffix),
    enabled: Boolean(enabled) && !!orgId && !!dutyId,
    staleTime: options?.staleTime ?? DEFAULT_DUTY_QUERY_STALE_TIME_MS,
    gcTime: options?.gcTime ?? DEFAULT_DUTY_QUERY_GC_TIME_MS,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    retry: options?.retry ?? 1,
    queryFn: async ({ signal }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      if (!dutyId) throw new Error('Missing duty id')
      return fetcher(token, dutyId, branch, signal)
    },
  })
}

function makeSimpleDutyHook<T>(suffix: string, fetcher: DutyFetcher<T>) {
  return (dutyId?: string) => useAuthedDutyQuery(dutyId, suffix, fetcher)
}

function makeEnabledDutyHook<T>(
  suffix: string,
  fetcher: DutyFetcher<T>,
  options?: AuthedDutyQueryOptions
) {
  return (dutyId?: string, enabled: boolean = true) =>
    useAuthedDutyQuery(dutyId, suffix, fetcher, enabled, options)
}

function makeSimpleDutyScriptHook<T>(
  suffix: string,
  fetcher: DutyScriptFetcher<T>
) {
  return (dutyId?: string) => useAuthedDutyScriptQuery(dutyId, suffix, fetcher)
}

function makeEnabledDutyScriptHook<T>(
  suffix: string,
  fetcher: DutyScriptFetcher<T>,
  options?: AuthedDutyQueryOptions
) {
  return (dutyId?: string, enabled: boolean = true) =>
    useAuthedDutyScriptQuery(dutyId, suffix, fetcher, enabled, options)
}

export const useDutyConnectionInfo = makeSimpleDutyHook(
  'connection',
  fetchDutyConnectionInfo
)
export const useDutyCallVolumeMonitor = makeEnabledDutyHook(
  'monitors/call-volume',
  fetchDutyCallVolumeMonitor
)

/**
 * Imperative fetch of a duty's connection info, for when a click handler rather
 * than a render needs it. Shares the cache key with `useDutyConnectionInfo`.
 */
export function useFetchDutyConnectionInfo() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return React.useCallback(
    async (dutyId: string) => {
      const orgId = organization?.id ?? null
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return queryClient.fetchQuery({
        queryKey: dutyQueryKey(orgId, dutyId, 'connection'),
        queryFn: () => fetchDutyConnectionInfo(token, dutyId),
        staleTime: DEFAULT_DUTY_QUERY_STALE_TIME_MS,
        gcTime: DEFAULT_DUTY_QUERY_GC_TIME_MS,
      })
    },
    [getToken, organization, queryClient]
  )
}

export const useDutyToolsInfo = makeSimpleDutyHook('tools', fetchDutyToolsInfo)

export function useDutyBookingProviders(
  dutyId?: string,
  search: string = '',
  enabled: boolean = true
) {
  const normalizedSearch = search.trim()
  return useAuthedDutyQuery(
    dutyId,
    `tools-config/booking/providers:${normalizedSearch}`,
    (token, id, signal) =>
      fetchDutyBookingProviders(token, id, normalizedSearch, signal),
    enabled
  )
}

export const useDutyWorkflows = makeSimpleDutyHook(
  'workflows',
  fetchDutyWorkflows
)

export const useDutyConversationMetrics = makeSimpleDutyScriptHook(
  'conversation',
  fetchDutyConversationMetrics
)

export const useDutySystemScript = makeEnabledDutyScriptHook(
  'system-script',
  fetchDutySystemScript,
  {
    // Prompt content changes infrequently and can be large; avoid needless refetching.
    staleTime: 5 * 60_000,
    retry: 1,
  }
)

export const useDutyTestPatientInfo = makeSimpleDutyHook(
  'test-patient',
  fetchDutyTestPatientInfo
)

export const useDutyVoicemailMetrics = makeSimpleDutyScriptHook(
  'voicemail',
  fetchDutyVoicemailMetrics
)

export const useDutyLabelMappingMetrics = makeSimpleDutyScriptHook(
  'label-mapping',
  fetchDutyLabelMappingMetrics
)

export const useDutyFactExtractionMetrics = makeSimpleDutyScriptHook(
  'fact-extraction',
  fetchDutyFactExtractionMetrics
)

export const useDutyFactsTemplate = makeSimpleDutyScriptHook(
  'facts-template',
  fetchDutyFactsTemplate
)

export const useDutyResequenceAnalysisPrompt = makeSimpleDutyScriptHook(
  'resequence-analysis-prompt',
  fetchDutyResequenceAnalysisPrompt
)

export const useDutySmsConfig = makeSimpleDutyHook(
  'messaging-prompts',
  fetchDutySmsConfig
)

export const useDutyTextingConfig = makeSimpleDutyHook(
  'texting-config',
  fetchDutyTextingConfig
)

export const useDutyVersions = makeEnabledDutyHook(
  'versions',
  fetchDutyVersions,
  { staleTime: 10_000, refetchOnWindowFocus: true }
)

export const useCompiledTemplates = makeEnabledDutyHook(
  'render-test-view',
  fetchCompiledTemplates,
  { staleTime: 60_000, retry: 1 }
)

// ---- Mutations (PATCH endpoints) ----

function useAuthedDutyMutation<TArgs, TResult>(
  dutyId: string,
  mutate: (token: string, dutyId: string, args: TArgs) => Promise<TResult>,
  options?: {
    /**
     * Extra query keys to invalidate alongside the standard
     * `['duty', orgId, dutyId]` scope, for a field that also drives
     * something outside the duty-scoped cache (e.g. `is_texting_only` and
     * `duty-summary`, which the canvas reads).
     */
    alsoInvalidate?: ReadonlyArray<readonly unknown[]>
  }
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null

  return useMutation({
    mutationFn: async (args: TArgs) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return mutate(token, dutyId, args)
    },
    onSuccess: async () => {
      // Invalidate all duty queries for this duty so cards refresh, plus any
      // extra keys the caller named. Wrapped in try/catch so an invalidation
      // failure doesn't surface as a "Save failed" error — the PATCH itself
      // already succeeded.
      try {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['duty', orgId, dutyId],
          }),
          ...(options?.alsoInvalidate ?? []).map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          ),
        ])
      } catch {
        // Cache refresh is best-effort; stale data will resolve on next focus/interval.
      }
    },
  })
}

function useAuthedDutyMutationRefreshingSnippets<TArgs, TResult>(
  dutyId: string,
  mutate: (token: string, dutyId: string, args: TArgs) => Promise<TResult>
) {
  const { organization } = useOrganization()

  return useAuthedDutyMutation(dutyId, mutate, {
    alsoInvalidate: [snippetKeys.all(organization?.id)],
  })
}

export function useUpdateDutyHandoffConfig(dutyId: string) {
  return useAuthedDutyMutation<
    { destinations: Record<string, unknown>[] },
    { success?: boolean; handoff_config?: unknown }
  >(dutyId, (token, id, args) =>
    patchDutyHandoffConfig({
      token,
      dutyId: id,
      destinations: args.destinations,
    })
  )
}

export function useUpdateDutyAuthenticatePersonConfig(dutyId: string) {
  return useAuthedDutyMutation<
    Record<string, unknown> | null,
    { success: boolean; config: Record<string, unknown> | null }
  >(dutyId, (token, id, config) =>
    patchDutyAuthenticatePersonConfig(token, id, config)
  )
}

export function useUpdateDutyBookingProviders(dutyId: string) {
  return useAuthedDutyMutation<
    {
      enabled_tools: BookingToolName[]
      facility_ids: string[]
      scheduling_instructions: string
      require_aligned_start?: boolean
      write_to_ehr?: boolean
      appointment_types?: AppointmentType[]
    },
    {
      success: boolean
      enabled_tools: BookingToolName[]
      facility_ids: string[]
      scheduling_instructions: string
      require_aligned_start: boolean
      write_to_ehr: boolean
    }
  >(dutyId, (token, id, config) => patchDutyBookingProviders(token, id, config))
}

/**
 * Enable/disable a single duty tool, persisted to the backend. Optimistically
 * updates the cached tools list so cards show/hide immediately, and rolls back
 * on failure.
 *
 * Tools in TOOL_WORKFLOW_SLUGS also register a Nova workflow for the duty
 * server-side. Their pill reads the duty `workflows` query as its source of
 * truth; patch both caches so the workflow UI and Mercury capability update
 * together before the refetch lands.
 */
export function useUpdateDutyTool(dutyId: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null
  const toolsQueryKey = dutyQueryKey(orgId, dutyId, 'tools')
  const workflowsQueryKey = dutyQueryKey(orgId, dutyId, 'workflows')

  return useMutation({
    mutationFn: async ({
      tool,
      enabled,
    }: {
      tool: DutyToolKey
      enabled: boolean
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return patchDutyTool(token, dutyId, tool, enabled)
    },
    onMutate: async ({ tool, enabled }) => {
      const slug = TOOL_WORKFLOW_SLUGS[tool]

      // Workflow-backed keys are not stored in app_duties.tools — the PATCH
      // moves only the Nebula registration — so predicting a change to the
      // tools list would be a guess the refetch then contradicts. Their pill
      // reads the `workflows` cache patched below instead.
      await queryClient.cancelQueries({ queryKey: toolsQueryKey })
      const previous = queryClient.getQueryData<DutyToolsInfo>(toolsQueryKey)
      if (previous && !slug) {
        const tools = enabled
          ? previous.tools.includes(tool)
            ? previous.tools
            : [...previous.tools, tool]
          : previous.tools.filter((t) => t !== tool)
        queryClient.setQueryData<DutyToolsInfo>(toolsQueryKey, {
          ...previous,
          tools,
        })
      }

      let previousWorkflows: DutyWorkflowsInfo | undefined
      if (slug) {
        await queryClient.cancelQueries({ queryKey: workflowsQueryKey })
        previousWorkflows =
          queryClient.getQueryData<DutyWorkflowsInfo>(workflowsQueryKey)
        if (previousWorkflows) {
          const existing = previousWorkflows.workflows.find(
            (workflow) => workflow.slug === slug
          )
          const others = previousWorkflows.workflows.filter(
            (w) => w.slug !== slug
          )
          // Slug is all the pill needs; the title/facts a card would show are
          // only known to Nebula, so the placeholder leaves them empty and the
          // refetch in onSettled swaps in the real row a moment later.
          queryClient.setQueryData<DutyWorkflowsInfo>(workflowsQueryKey, {
            workflows: enabled
              ? [
                  ...others,
                  existing ?? {
                    slug,
                    org_slug: null,
                    title: null,
                    trigger: null,
                    description: null,
                    // A workflow being enabled is one the org can still run, so
                    // the placeholder reads as live until the refetch confirms.
                    deprecated: false,
                    facts_schema: null,
                  },
                ]
              : others,
          })
        }
      }

      return { previous, previousWorkflows }
    },
    onError: (_err, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(toolsQueryKey, context.previous)
      }
      if (context?.previousWorkflows) {
        queryClient.setQueryData(workflowsQueryKey, context.previousWorkflows)
      }
    },
    onSettled: async (_data, _error, { tool }) => {
      // Refresh all duty queries so dependent cards reflect the new state.
      // Wrapped in try/catch so an invalidation failure doesn't surface as a
      // toggle error — the PATCH itself already succeeded.
      try {
        const invalidations = [
          queryClient.invalidateQueries({
            queryKey: ['duty', orgId, dutyId],
          }),
        ]
        if (TOOL_WORKFLOW_SLUGS[tool]) {
          // `list` (which workflows link to a duty), never `all`: the toggle
          // moves a row in app_nova_workflow_duties, and `all` would prefix-
          // match `available` too — Nebula's org catalogue, read from YAML on
          // disk, which a duty toggle cannot change and which is cached for an
          // hour precisely so duty-after-duty browsing shares one fetch.
          invalidations.push(
            queryClient.invalidateQueries({
              queryKey: workflowKeys.list(orgId ?? undefined),
            })
          )
        }
        await Promise.all(invalidations)
      } catch {
        // Cache refresh is best-effort; stale data will resolve on next focus/interval.
      }
    },
  })
}

export function useUpdateDutyHandoffToDutyConfig(dutyId: string) {
  return useAuthedDutyMutation<
    Record<string, unknown> | null,
    { success: boolean; config: Record<string, unknown> | null }
  >(dutyId, (token, id, config) =>
    patchDutyHandoffToDutyConfig(token, id, config)
  )
}

export function useUpdateDutyConversationScript(dutyId: string) {
  return useAuthedDutyMutationRefreshingSnippets<string, { script: string }>(
    dutyId,
    (token, id, script) => patchDutyConversationScript(token, id, script)
  )
}

export function useUpdateDutyVoicemailScript(dutyId: string) {
  return useAuthedDutyMutationRefreshingSnippets<string, { script: string }>(
    dutyId,
    (token, id, script) => patchDutyVoicemailScript(token, id, script)
  )
}

export function useUpdateDutyLabelMappingScript(dutyId: string) {
  return useAuthedDutyMutation<string, { script: string }>(
    dutyId,
    (token, id, script) => patchDutyLabelMappingScript(token, id, script)
  )
}

export function useUpdateDutyFactExtractionScript(dutyId: string) {
  return useAuthedDutyMutation<
    string,
    { facts_as_string: string; facts_count: number }
  >(dutyId, (token, id, script) =>
    patchDutyFactExtractionScript(token, id, script)
  )
}

export function useUpdateDutyFactsTemplate(dutyId: string) {
  return useAuthedDutyMutation<string, { template: string }>(
    dutyId,
    (token, id, script) => patchDutyFactsTemplateScript(token, id, script)
  )
}

export function useUpdateDutyResequenceAnalysisPrompt(dutyId: string) {
  return useAuthedDutyMutation<string, { prompt: string }>(
    dutyId,
    (token, id, prompt) => patchDutyResequenceAnalysisPrompt(token, id, prompt)
  )
}

export function useUpdateDutySmsConfig(dutyId: string) {
  return useAuthedDutyMutation<Partial<DutySmsConfig>, DutySmsConfig>(
    dutyId,
    (token, id, data) => patchDutySmsConfig(token, id, data)
  )
}

/**
 * Unlike other duty mutations, this one also invalidates the duty-summary
 * query -- `is_texting_only` drives which canvas block
 * (`DutyWorkflowCanvas`) renders, so a toggle needs to flip that live rather
 * than waiting for an unrelated summary refetch.
 */
export function useUpdateDutyTextingConfig(dutyId: string) {
  const { organization } = useOrganization()
  const orgId = organization?.id ?? null

  return useAuthedDutyMutation<Partial<DutyTextingConfig>, DutyTextingConfig>(
    dutyId,
    (token, id, data) => patchDutyTextingConfig(token, id, data),
    { alsoInvalidate: [dutySummaryKeys.detail(orgId, dutyId)] }
  )
}

export function useUpdateDutySystemScript(dutyId: string) {
  return useAuthedDutyMutationRefreshingSnippets<
    DutySystemScriptPatchArgs,
    DutySystemScriptPayload
  >(dutyId, (token, id, args) => patchDutySystemScript(token, id, args))
}

export function useUpdateDutyConnection(dutyId: string) {
  return useAuthedDutyMutation<PatchDutyConnectionPayload, DutyConnectionInfo>(
    dutyId,
    (token, id, data) => patchDutyConnection(token, id, data)
  )
}

export function useUpdateDutyTestPatient(dutyId: string) {
  return useAuthedDutyMutation<
    PatchDutyTestPatientPayload,
    DutyTestFactsSaveCheck
  >(dutyId, (token, id, data) => patchDutyTestPatient(token, id, data))
}

export function useSetDutyStatus(dutyId: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null
  const summaryKey = dutySummaryKeys.detail(orgId, dutyId)

  return useMutation({
    mutationFn: async (status: DutyStatus) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return patchDutyStatus(token, dutyId, status)
    },
    onMutate: async (status) => {
      await queryClient.cancelQueries({ queryKey: summaryKey })
      const prev = queryClient.getQueryData<DutySummary>(summaryKey)
      if (prev) {
        queryClient.setQueryData<DutySummary>(summaryKey, { ...prev, status })
      }
      return { prev }
    },
    onError: (_err, _status, context) => {
      if (context?.prev) {
        queryClient.setQueryData<DutySummary>(summaryKey, context.prev)
      }
    },
    onSettled: async () => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['duties'] }),
          queryClient.invalidateQueries({ queryKey: ['duty', orgId, dutyId] }),
          queryClient.invalidateQueries({ queryKey: summaryKey }),
        ])
      } catch {
        // best-effort
      }
    },
  })
}

export function useUpdateDutyName(dutyId: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null
  const summaryKey = dutySummaryKeys.detail(orgId, dutyId)

  return useMutation({
    mutationFn: async (name: string) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return patchDutyName(token, dutyId, name)
    },
    onMutate: async (name) => {
      await queryClient.cancelQueries({ queryKey: summaryKey })
      const prev = queryClient.getQueryData<DutySummary>(summaryKey)
      if (prev) {
        queryClient.setQueryData<DutySummary>(summaryKey, { ...prev, name })
      }
      return { prev }
    },
    onError: (_err, _name, context) => {
      if (context?.prev) {
        queryClient.setQueryData<DutySummary>(summaryKey, context.prev)
      }
    },
    onSettled: async () => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['duty', orgId, dutyId],
          }),
          queryClient.invalidateQueries({ queryKey: summaryKey }),
        ])
      } catch {
        // best-effort
      }
    },
  })
}

export const useDutyWaypoints = makeSimpleDutyHook(
  'waypoints',
  fetchDutyWaypoints
)

export function useUpdateDutyWaypoints(dutyId: string) {
  return useAuthedDutyMutation<
    import('@/features/duties/types').Waypoint[],
    { waypoints: import('@/features/duties/types').DutyWaypoints }
  >(dutyId, (token, id, waypoints) => patchDutyWaypoints(token, id, waypoints))
}

export const useDutyAssistantOverrides = makeEnabledDutyHook(
  'assistant-overrides',
  fetchDutyAssistantOverrides
)

export const useDutyTestAssistantOverrides = makeEnabledDutyHook(
  'assistant-overrides-test',
  fetchDutyTestAssistantOverrides
)

export function useUpdateDutyAssistantOverrides(dutyId: string) {
  return useAuthedDutyMutation<DutyAssistantOverridesPatchArgs, void>(
    dutyId,
    (token, id, args) => patchDutyAssistantOverrides(token, id, args, false)
  )
}

export function useUpdateDutyTestAssistantOverrides(dutyId: string) {
  return useAuthedDutyMutation<DutyAssistantOverridesPatchArgs, void>(
    dutyId,
    (token, id, args) => patchDutyAssistantOverrides(token, id, args, true)
  )
}

export const useDutyFirstMessage = makeEnabledDutyHook(
  'first-message',
  fetchDutyFirstMessage
)

export const useDutyPatientMemory = makeSimpleDutyHook(
  'patient-memory',
  fetchDutyPatientMemory
)

export const useDutyTestMemories = makeEnabledDutyHook(
  'test-memories',
  fetchDutyTestMemories,
  { retry: 0 }
)

/** The duty's read-only client-selection criteria. */
export const useDutyAssignmentLogic = makeSimpleDutyHook(
  ASSIGNMENT_LOGIC_QUERY,
  fetchDutyAssignmentLogic
)

/** The newest run of the duty's assignment rules, or null if none is recorded. */
export const useDutyLastAssignmentRun = makeSimpleDutyHook(
  LAST_ASSIGNMENT_RUN_QUERY,
  fetchDutyLastAssignmentRun
)

/** The newest run where the assignment rule moved someone, or null if none is recorded. */
export const useDutyLastAssignmentChange = makeEnabledDutyHook(
  LAST_ASSIGNMENT_CHANGE_QUERY,
  fetchDutyLastAssignmentChange
)

/** The newest run where the exclusion rule moved someone, or null if none is recorded. */
export const useDutyLastExclusionChange = makeEnabledDutyHook(
  LAST_EXCLUSION_CHANGE_QUERY,
  fetchDutyLastExclusionChange
)

/** The duty's read-only client-exclusion criteria. */
export const useDutyExclusionLogic = makeSimpleDutyHook(
  EXCLUSION_LOGIC_QUERY,
  fetchDutyExclusionLogic
)

export function useUpdateDutyFirstMessage(dutyId: string) {
  return useAuthedDutyMutation<string | null, string | null>(
    dutyId,
    (token, id, firstMessage) => patchDutyFirstMessage(token, id, firstMessage)
  )
}

export function useUpdateDutyPatientMemory(dutyId: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null
  const queryKey = dutyQueryKey(orgId, dutyId, 'patient-memory')

  return useMutation({
    mutationFn: async (config: DutyPatientMemoryPatch) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return patchDutyPatientMemory(token, dutyId, config)
    },
    onSuccess: (detail) => {
      // The successful PATCH is authoritative. An immediate refetch could hit
      // another Hoku worker's short-lived duty-detail cache and restore the old
      // value in React Query.
      queryClient.setQueryData<DutyPatientMemoryDetail>(queryKey, detail)
    },
  })
}

export function useUpdateDutyTestMemories(dutyId: string) {
  return useAuthedDutyMutation<TestMemoryEntry[], DutyTestMemories>(
    dutyId,
    (token, id, entries) => patchDutyTestMemories(token, id, entries)
  )
}

export function useGenerateDutyWaypoints(dutyId: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null

  return useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return generateDutyWaypoints(token, dutyId)
    },
    onSuccess: async () => {
      try {
        await queryClient.invalidateQueries({
          queryKey: ['duty', orgId, dutyId],
        })
      } catch {
        // best-effort
      }
    },
  })
}

/**
 * Start a Nova session for one of the duty's registered workflows.
 *
 * No cache to update — a session is new state living in Nebula, not a change to
 * anything the duty cards render — so this deliberately skips the invalidation
 * the other duty mutations do.
 */
export function useInvokeDutyWorkflow(dutyId: string) {
  const { getToken } = useAuth()

  return useMutation({
    mutationFn: async ({
      slug,
      facts,
    }: {
      slug: string
      facts: Record<string, string>
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return invokeDutyWorkflow({ token, dutyId, slug, facts })
    },
  })
}

// ---- Workflow variants ----

/** Cache suffix for one workflow's variants — per slug, since the card asks
 *  about a single workflow and two cards must not share a cache entry. */
function variantsSuffix(slug: string) {
  return `workflow-variants/${slug}`
}

function isWorkflowVariantsQuery(
  query: Query,
  orgId: string | null,
  slug: string
): boolean {
  const key = query.queryKey
  return (
    key[0] === 'duty' && key[1] === orgId && key[3] === variantsSuffix(slug)
  )
}

function invalidateWorkflowVariants(
  queryClient: QueryClient,
  orgId: string | null,
  slug: string
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => isWorkflowVariantsQuery(query, orgId, slug),
  })
}

/**
 * The org's variants of one workflow, each flagged with whether this duty has
 * it enabled. Org-scoped data on a duty-scoped key: `enabled` is answered for
 * this duty, so the entry can't be shared across duties.
 */
export function useDutyWorkflowVariants(
  dutyId: string | undefined,
  slug: string
) {
  const fetcher = React.useCallback<DutyFetcher<WorkflowVariantsInfo>>(
    (token, id, signal) => fetchDutyWorkflowVariants(token, id, slug, signal),
    [slug]
  )
  return useAuthedDutyQuery(dutyId, variantsSuffix(slug), fetcher)
}

/**
 * Create or replace an org-wide variant.
 *
 * The acting duty's cache takes the returned row immediately, then every cache
 * of this workflow is invalidated in the background. The invalidation is also
 * started on error: the write may have landed before a response was lost, and
 * a stale list would make the next create-only attempt report an invisible key
 * collision. It is deliberately not awaited so the editor can close as soon as
 * the save response arrives, matching the delete flow below.
 */
export function useUpsertWorkflowVariant(dutyId: string, slug: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null
  const variantsKey = dutyQueryKey(orgId, dutyId, variantsSuffix(slug))

  const refresh = () => {
    void invalidateWorkflowVariants(queryClient, orgId, slug).catch(() => {})
  }

  return useMutation({
    mutationFn: async (variant: UpsertVariantPayload) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return upsertDutyWorkflowVariant({ token, dutyId, slug, variant })
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<WorkflowVariantsInfo>(
        variantsKey,
        (previous) => {
          if (!previous) return previous
          const existingIndex = previous.variants.findIndex(
            (variant) => variant.variant_key === saved.variant_key
          )
          if (existingIndex === -1) {
            return { variants: [...previous.variants, saved] }
          }
          return {
            variants: previous.variants.map((variant, index) =>
              index === existingIndex ? saved : variant
            ),
          }
        }
      )
      refresh()
    },
    onError: refresh,
  })
}

/**
 * Delete a variant.
 *
 * Not optimistic — this is destructive and org-wide, so nothing changes until
 * the server confirms. Once it does, the row is dropped from the cached list
 * here rather than waited for: this deliberately does NOT use
 * `useAuthedDutyMutation`, whose `onSuccess` awaits `invalidateQueries`.
 * React Query runs a mutation's own `onSuccess` to completion before the
 * per-call one, so awaiting the refetch there would hold the editor open —
 * and its `onSuccess` is what closes it — until the refetch landed. The
 * invalidation still runs, just unawaited, to reconcile in the background.
 */
export function useDeleteWorkflowVariant(dutyId: string, slug: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null

  return useMutation({
    mutationFn: async ({ variantKey }: { variantKey: string }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return deleteDutyWorkflowVariant({ token, dutyId, slug, variantKey })
    },
    onSuccess: ({ variantKey }) => {
      queryClient.setQueriesData<WorkflowVariantsInfo>(
        {
          predicate: (query) => isWorkflowVariantsQuery(query, orgId, slug),
        },
        (previous) =>
          previous
            ? {
                variants: previous.variants.filter(
                  (variant) => variant.variant_key !== variantKey
                ),
              }
            : previous
      )
      // Unawaited on purpose (see above). Active lists refetch immediately;
      // inactive ones stay marked stale and refetch when mounted again.
      void invalidateWorkflowVariants(queryClient, orgId, slug).catch(() => {})
    },
    onError: () => {
      // The delete may have landed before its response was lost.
      void invalidateWorkflowVariants(queryClient, orgId, slug).catch(() => {})
    },
  })
}

/**
 * Turn a variant on or off for this duty.
 *
 * Optimistic, like the tool pills: the switch is the whole interaction, so
 * waiting a round trip to move it reads as a dropped click. Rolls back on
 * failure and refetches either way.
 */
export function useSetWorkflowVariantEnabled(dutyId: string, slug: string) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const orgId = organization?.id ?? null
  const variantsKey = dutyQueryKey(orgId, dutyId, variantsSuffix(slug))

  return useMutation({
    mutationFn: async ({
      variantKey,
      enabled,
    }: {
      variantKey: string
      enabled: boolean
    }) => {
      const token = await getToken()
      if (!token) throw new Error(NO_AUTH_TOKEN_ERROR)
      return setDutyWorkflowVariantEnabled({
        token,
        dutyId,
        slug,
        variantKey,
        enabled,
      })
    },
    onMutate: async ({ variantKey, enabled }) => {
      await queryClient.cancelQueries({ queryKey: variantsKey })
      const previous =
        queryClient.getQueryData<WorkflowVariantsInfo>(variantsKey)
      if (previous) {
        queryClient.setQueryData<WorkflowVariantsInfo>(variantsKey, {
          variants: previous.variants.map((variant) =>
            variant.variant_key === variantKey
              ? variant.enabled === enabled
                ? variant
                : {
                    ...variant,
                    enabled,
                    duty_count: Math.max(
                      0,
                      variant.duty_count + (enabled ? 1 : -1)
                    ),
                  }
              : variant
          ),
        })
      }
      return { previous }
    },
    onError: (_error, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(variantsKey, context.previous)
      }
    },
    onSettled: async () => {
      try {
        await invalidateWorkflowVariants(queryClient, orgId, slug)
      } catch {
        // Cache refresh is best-effort; stale data resolves on next focus.
      }
    },
  })
}

// ---- Prefetch helper for list views ----

export function usePrefetchDutyDetail() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  const prefetchers = React.useMemo<
    Array<{ suffix: string; fetcher: DutyFetcher<unknown> }>
  >(
    () => [
      { suffix: 'connection', fetcher: fetchDutyConnectionInfo },
      { suffix: 'tools', fetcher: fetchDutyToolsInfo },
      { suffix: 'test-patient', fetcher: fetchDutyTestPatientInfo },
    ],
    []
  )

  return React.useCallback(
    async (dutyId: string | undefined | null) => {
      if (!organization?.id || !dutyId) return

      const token = await getToken()
      if (!token) return

      const orgId = organization.id

      await Promise.allSettled(
        prefetchers.map(({ suffix, fetcher }) =>
          queryClient.prefetchQuery({
            queryKey: dutyQueryKey(orgId, dutyId, suffix),
            queryFn: () => fetcher(token, dutyId),
            staleTime: DEFAULT_DUTY_QUERY_STALE_TIME_MS,
            gcTime: DEFAULT_DUTY_QUERY_GC_TIME_MS,
          })
        )
      )
    },
    [getToken, organization, prefetchers, queryClient]
  )
}
